import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { Check, Copy, Download, Image as ImageIcon, QrCode, X } from 'lucide-react';

type Props = {
  /** The booking URL the QR code points at. */
  url: string;
  /** Shown as the modal heading, e.g. "Downtown location". */
  title: string;
  /** Base name for downloaded files (no extension). */
  filename: string;
  /** Class on the trigger button — match the surrounding button style. */
  className?: string;
  /** Optional caveat shown under the code (e.g. when the link may not work yet). */
  note?: string;
  /** Render the trigger as an icon only (for dense table rows). */
  iconOnly?: boolean;
};

// Print-friendly defaults: plain black-on-white with a quiet zone, medium error
// correction. Brand colors hurt scan reliability on cheap printers, so we
// don't tint the code.
const QR_OPTS = { errorCorrectionLevel: 'M', margin: 2 } as const;
const PNG_WIDTH = 1024;

/**
 * Trigger button + modal that renders a QR code for a public booking link,
 * with copy/download actions for sharing digitally or printing on business
 * cards, flyers, front-desk signage, etc. Generated entirely client-side.
 */
export default function QrCodeButton({ url, title, filename, note, className, iconOnly }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => setOpen(true)}
        title="QR code"
        aria-label={iconOnly ? `QR code for ${title}` : undefined}
      >
        <QrCode size={iconOnly ? 16 : 14} />
        {!iconOnly && ' QR code'}
      </button>
      {/* Portal so the fixed overlay escapes table rows / filtered ancestors. */}
      {open &&
        createPortal(
          <QrModal url={url} title={title} filename={filename} note={note} onClose={() => setOpen(false)} />,
          document.body,
        )}
    </>
  );
}

function QrModal({
  url,
  title,
  filename,
  note,
  onClose,
}: {
  url: string;
  title: string;
  filename: string;
  note?: string;
  onClose: () => void;
}) {
  const [svg, setSvg] = useState<string | null>(null);
  const [png, setPng] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<'link' | 'image' | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      QRCode.toString(url, { ...QR_OPTS, type: 'svg' }),
      QRCode.toDataURL(url, { ...QR_OPTS, width: PNG_WIDTH }),
    ])
      .then(([s, p]) => {
        if (cancelled) return;
        setSvg(s);
        setPng(p);
      })
      .catch((e) => !cancelled && setErr((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function flash(kind: 'link' | 'image') {
    setCopied(kind);
    setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1500);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      flash('link');
    } catch {
      alert(`Copy this link manually:\n${url}`);
    }
  }

  async function copyImage() {
    if (!png) return;
    try {
      // Pass a promise (not a resolved Blob) so Safari keeps the user-gesture.
      const blob = fetch(png).then((r) => r.blob());
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      flash('image');
    } catch {
      setErr("Your browser blocked copying the image. Use Download PNG instead.");
    }
  }

  function download(href: string, ext: 'png' | 'svg') {
    const a = document.createElement('a');
    a.href = href;
    a.download = `${filename}-qr.${ext}`;
    a.click();
  }

  function downloadSvg() {
    if (!svg) return;
    const href = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    download(href, 'svg');
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  }

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-card qr-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qr-modal-title"
      >
        <button type="button" className="ghost modal-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h2 id="qr-modal-title" style={{ marginTop: 0 }}>
          {title}
        </h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Scanning this opens the booking page. Download the SVG for print
          (business cards, signage) &mdash; it stays sharp at any size.
        </p>

        <div className="qr-frame">
          {png ? <img src={png} alt={`QR code linking to ${url}`} /> : <span className="muted">Generating…</span>}
        </div>

        {note && <p className="warn-text">{note}</p>}

        <div className="url-display" style={{ marginBottom: '1rem' }}>
          <code>{url}</code>
          <button type="button" onClick={copyLink}>
            {copied === 'link' ? <Check size={14} /> : <Copy size={14} />}
            {copied === 'link' ? 'Copied' : 'Copy link'}
          </button>
        </div>

        {err && <p className="err">{err}</p>}

        <div className="row qr-actions">
          <button type="button" onClick={copyImage} disabled={!png}>
            {copied === 'image' ? <Check size={14} /> : <ImageIcon size={14} />}
            {copied === 'image' ? 'Copied' : 'Copy image'}
          </button>
          <button type="button" onClick={() => png && download(png, 'png')} disabled={!png}>
            <Download size={14} /> PNG
          </button>
          <button type="button" className="primary" onClick={downloadSvg} disabled={!svg}>
            <Download size={14} /> SVG (print)
          </button>
        </div>
      </div>
    </div>
  );
}
